import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  formatCostPerSecond,
  formatLatencyMs,
  modelOptionLabel,
  type VideoModelInfo,
} from "../lib/videoModels";

interface Props {
  value: string;
  onChange: (modelId: string) => void;
  autoLoad?: boolean;
}

export function VideoModelSelect({
  value,
  onChange,
  autoLoad = true,
}: Props) {
  const [models, setModels] = useState<VideoModelInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ models: VideoModelInfo[] }>("/config/openrouter/models");
      const list = Array.isArray(res?.models) ? res.models : [];
      list.sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id));
      setModels(list);
      if (!list.length) setError("No video models returned.");
    } catch (err: any) {
      setError(err?.details?.message ?? err?.message ?? "Failed to load models");
      setModels([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (autoLoad) load();
  }, [autoLoad]);

  const selected = models.find((m) => m.id === value) ?? null;
  const known = !value || models.some((m) => m.id === value);

  return (
    <div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading && models.length === 0}
      >
        {!value && <option value="">Select a model…</option>}
        {!known && value && (
          <option value={value}>{value} (saved)</option>
        )}
        {models.map((m) => (
          <option key={m.id} value={m.id}>
            {modelOptionLabel(m)}
          </option>
        ))}
      </select>
      <div className="row" style={{ marginTop: 6, flexWrap: "wrap", gap: 8 }}>
        <button type="button" disabled={loading} onClick={load}>
          {loading ? "Loading…" : models.length ? "Refresh models" : "Load models"}
        </button>
        {models.length > 0 && (
          <span className="tiny muted">{models.length} models</span>
        )}
        {error && (
          <span className="tiny" style={{ color: "var(--danger)" }}>{error}</span>
        )}
      </div>
      {selected && (
        <div className="tiny muted" style={{ marginTop: 6 }}>
          Avg cost {formatCostPerSecond(selected.avgCostPerSecond)}
          {selected.minCostPerSecond != null &&
            selected.maxCostPerSecond != null &&
            Math.abs(selected.maxCostPerSecond - selected.minCostPerSecond) > 0.001 && (
              <> (range {formatCostPerSecond(selected.minCostPerSecond)}–{formatCostPerSecond(selected.maxCostPerSecond)})</>
            )}
          {" · "}
          Latency {formatLatencyMs(selected.avgLatencyMs)}
          {selected.latencySampleCount
            ? ` from ${selected.latencySampleCount} local run${selected.latencySampleCount === 1 ? "" : "s"}`
            : " (no local runs yet — OpenRouter doesn’t publish video latency)"}
        </div>
      )}
    </div>
  );
}
